import { roboto } from '@/font/index';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { alpha, createTheme, ThemeOptions } from '@mui/material/styles';
import { CustomGrey } from '@/types/CustomColors';

const baseTheme = createTheme({
	palette: {
		red: {
			main: 'rgba(238, 42, 36, 1)', //#EE2A24
			light: 'rgba(253, 234, 233, 1)', //#FDEAE9
		},
		green: {
			main: 'rgba(4, 142, 57, 1)', //#048E39
			light: 'rgba(230, 244, 235, 1)', //#E6F4EB
		},
		yellow: {
			main: 'rgba(250, 180, 0, 1)', //#FAB400
			medium: 'rgba(251, 193,23,1)', //#FBC117
			light: 'rgba(255, 248, 230, 1)', //#FFF8E6
		},
		orange: {
			main: 'rgba(250, 107, 0, 1)', //#FA6B00
		},
		black: {
			main: 'rgba(0, 0, 0, 1)', //#000000
			brand: 'rgba(35, 31, 32, 1)', //#231F20
		},
		grey: {
			ultraDark: 'rgba(56, 56, 56, 1)', //#383838
			dark: 'rgba(113, 112, 115, 1)', //#717073
			main: 'rgba(145, 145, 149, 1)', //#919195
			medium: 'rgba(186, 188, 190, 1)', //#BABCBE
			light: 'rgba(216, 217, 218, 1)', //#D8D9DA
			ultraLight: 'rgba(245, 245, 245, 1)', //#F5F5F5
		},
		white: {
			main: 'rgba(255, 255, 255, 1)', //#FFFFFF
		},
		blue: {
			main: 'rgba(30,113,183, 1)', //#1E71B7
		},
	},
	typography: {
		fontFamily: roboto.style.fontFamily,
		h0: {
			fontSize: '46px',
			lineHeight: '50px',
		},
		h1: {
			fontSize: '36px',
			fontWeight: 500,
			lineHeight: '42px',
		},
		h2: {
			fontSize: '28px',
			fontWeight: 500,
			lineHeight: '32px',
		},
		h3: {
			fontSize: '24px',
			fontWeight: 500,
			lineHeight: '28px',
		},
		h4: {
			fontSize: '20px',
			fontWeight: 500,
			lineHeight: '26px',
		},
		h5: {
			fontSize: '18px',
			fontWeight: 500,
			lineHeight: '24px',
		},
		h6: {
			fontSize: '16px',
			fontWeight: 500,
			lineHeight: '20px',
		},
		h7: {
			fontSize: '14px',
			fontWeight: 400,
			lineHeight: '18px',
		},
		h7SemiBold: {
			fontSize: '14px',
			fontWeight: 500,
			lineHeight: '18px',
		},
		priceTiles: {
			fontSize: '22px',
			fontWeight: 500,
			lineHeight: '26px',
		},
		title: {
			fontSize: '16px',
			fontWeight: 500,
			lineHeight: '24px',
		},
		paragraph: {
			fontSize: '16px',
			fontWeight: 400,
			lineHeight: '24px',
		},
		productInfo: {
			fontSize: '14px',
			fontWeight: 400,
			lineHeight: '18px',
		},
		legal: {
			fontSize: '12px',
			fontWeight: 400,
			lineHeight: '18px',
		},
		legalBold: {
			fontSize: '12px',
			fontWeight: 700,
			lineHeight: '18px',
		},
		paragraphBold: {
			fontSize: '16px',
			fontWeight: 600,
			lineHeight: '24px',
		},
		paragraphSemiBold: {
			fontSize: '16px',
			fontWeight: 500,
			lineHeight: '24px',
		},
		paragraphExtraBold: {
			fontSize: '16px',
			fontWeight: 700,
			lineHeight: '24px',
		},
		h5ExtraBold: {
			fontSize: '18px',
			fontWeight: 700,
			lineHeight: '24px',
		},
		breadcrumb: {
			fontSize: '13px',
			fontWeight: 400,
			lineHeight: '16px',
		},
	},
	breakpoints: {
		values: {
			xs: 0,
			sm: 600,
			md: 960,
			lg: 1200,
			xl: 1320,
			xxl: 1420,
		},
	},
	components: {
		MuiLink: {
			styleOverrides: {
				root: {
					fontWeight: 500,
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					'&:hover': {
						backgroundColor: 'transparent',
					},
				},
			},
		},
		MuiBadge: {
			styleOverrides: {
				colorError: ({ theme }) => ({
					backgroundColor: theme.palette.red.main,
				}),
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					borderRadius: '100px',
					padding: '12px 24px',
					textTransform: 'none',
				},
				contained: ({ theme }) => ({
					backgroundColor: theme.palette.primary.main,
					boxShadow: 'none',
					'&:hover': {
						boxShadow: 'none',
					},
					'&:disabled': {
						color: theme.palette.text.disabled,
					},
				}),
				outlined: ({ theme }) => ({
					color: theme.palette.primary.main,
					'&:hover': {
						backgroundColor: alpha(theme.palette.secondary.main, 0.04),
					},
					'&:focus': {
						backgroundColor: alpha(theme.palette.secondary.main, 0.04),
					},
				}),
			},
		},
		MuiFab: {
			styleOverrides: {
				root: {
					boxShadow: 'none',
					'&:hover': {
						boxShadow: 'none',
					},
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: ({ theme }) => ({
					'--TextField-brandBorderColor': (theme.palette.grey as CustomGrey).ultraDark,
					'--TextField-brandBorderHoverColor': (theme.palette.grey as CustomGrey).dark,
					'--TextField-brandBorderFocusedColor': theme.palette.primary.main,
					'--TextField-brandBorderDisabledHoverColor': (theme.palette.grey as CustomGrey).medium,
					'& .MuiOutlinedInput-root': {
						'&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
							borderColor: 'var(--TextField-brandBorderDisabledHoverColor)',
						},
					},
					'& label': {
						color: 'var(--TextField-brandBorderColor)',
					},
					'& label.Mui-focused': {
						color: 'var(--TextField-brandBorderFocusedColor)',
					},
				}),
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				notchedOutline: {
					borderColor: 'var(--TextField-brandBorderColor)',
				},
				root: {
					[`&:hover .${outlinedInputClasses.notchedOutline}`]: {
						borderColor: 'var(--TextField-brandBorderHoverColor)',
					},
					[`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
						borderColor: 'var(--TextField-brandBorderFocusedColor)',
					},
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					padding: '0px',
				},
			},
		},
		MuiAlert: {
			styleOverrides: {
				outlinedSuccess: ({ theme }) => ({
					border: `1px solid ${theme.palette.primary.main}`,
					backgroundColor: 'white',
					color: theme.palette.black.main,
				}),
				outlinedError: ({ theme }) => ({
					backgroundColor: 'white',
					color: theme.palette.black.main,
				}),
				outlinedInfo: ({ theme }) => ({
					backgroundColor: 'white',
					color: theme.palette.black.main,
				}),
				outlinedWarning: ({ theme }) => ({
					border: `1px solid ${theme.palette.yellow.main}`,
					backgroundColor: 'white',
					color: theme.palette.black.main,
					'.MuiAlert-icon': {
						color: theme.palette.yellow.main,
					},
				}),
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: ({ theme }) => ({
					cursor: 'pointer',
					'&:hover': {
						backgroundColor: theme.palette.accent.main,
					},
					'&:nth-of-type(even):not(:hover)': {
						backgroundColor: theme.palette.lightAccent?.main ?? (theme.palette.grey as CustomGrey).ultraLight,
					},
				}),
				head: {
					cursor: 'default',
					'&:hover': {
						backgroundColor: 'inherit',
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					borderBottom: 'none',
				},
				head: ({ theme }) => ({
					borderTop: `1px solid ${(theme.palette.grey as CustomGrey).medium}`,
					borderBottom: `1px solid ${(theme.palette.grey as CustomGrey).medium}`,
					fontWeight: 700,
				}),
			},
		},
		MuiChip: {
			styleOverrides: {
				root: ({ theme }) => ({
					border: `1px solid ${(theme.palette.grey as CustomGrey).dark}`,
					background: (theme.palette.grey as CustomGrey).ultraLight,
					borderRadius: 8,
				}),
				label: {
					fontSize: '14px',
					fontWeight: 500,
				},
				deleteIcon: ({ theme }) => ({
					color: theme.palette.black.main,
				}),
			},
		},
	},
} as ThemeOptions);

const responsiveBaseTheme = createTheme(baseTheme, {
	typography: {
		h0: {
			[baseTheme.breakpoints.down('md')]: {
				fontSize: '34px',
				lineHeight: '40px',
			},
		},
		h1: {
			[baseTheme.breakpoints.down('md')]: {
				fontSize: '26px',
				lineHeight: '30px',
			},
		},
		h2: {
			[baseTheme.breakpoints.down('md')]: {
				fontSize: '22px',
				lineHeight: '28px',
			},
		},
		h3: {
			[baseTheme.breakpoints.down('md')]: {
				fontSize: '20px',
				lineHeight: '26px',
			},
		},
		h4: {
			[baseTheme.breakpoints.down('md')]: {
				fontSize: '18px',
				lineHeight: '24px',
			},
		},
		h5: {
			[baseTheme.breakpoints.down('md')]: {
				fontSize: '18px',
				lineHeight: '24px',
			},
		},
		h6: {
			[baseTheme.breakpoints.down('md')]: {
				fontSize: '16px',
				lineHeight: '20px',
			},
		},
		h7: {
			[baseTheme.breakpoints.down('md')]: {
				fontSize: '14px',
				lineHeight: '18px',
			},
		},
	},
} as ThemeOptions);

export default responsiveBaseTheme;
